import { POStatusTypes } from "@/api_framework/api_modals/Inventory";
import PORaiseErrorModal from "@/app/components/routes/modals/PORaiseErrorModal";
import AlertError from "@/app/components/ui/Alerts/Error";
import Input from "@/app/components/ui/Inputs/Input";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { ArrowRightIcon } from "@heroicons/react/20/solid";
import { ClockIcon } from "@heroicons/react/24/outline";
import { isObject } from "lodash";
import React, { useEffect, useState } from "react";
import { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import { BiImage } from "react-icons/bi";
import { FaRegFilePdf } from "react-icons/fa6";
import { IoMdEye } from "react-icons/io";
import { IInventoryPurchaseOrderFormFields } from "..";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import { format, fromUnixTime, parseISO } from "date-fns";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import ImageUploadComponent from "@/app/components/ui/ImageUploadComponent";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useExtractInvoiceDetailsAPI } from "@/app/hooks/api_hooks/Inventory/PO/useExtractInvoiceDetailsAPI";
import { INVOICE_DATE_AND_NUMBER_EDITABLE_STATUSES_FOR_PO } from "../../constants";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

interface IManageInvoiceFormFieldsProps {
  onSubmit: (overrideNextPOStatus?: POStatusTypes) => void;
  formHook: UseFormReturn<IInventoryPurchaseOrderFormFields, any>;
  attachementFieldHook: UseFieldArrayReturn<
    IInventoryPurchaseOrderFormFields,
    "attachments" | "product_info" | "invoice_details.attachments",
    "id"
  >;
  
  isUploading: boolean;
  setIsUploading: (value: boolean) => void;
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Day the PO was raised, as `yyyy-MM-dd`. `created_at` is epoch seconds. */
const getPODate = (createdAt?: number | string | null): string | null => {
  const seconds = Number(createdAt);
  return seconds > 0 ? format(fromUnixTime(seconds), "yyyy-MM-dd") : null;
};

/** An invoice can't predate its PO. Both are `yyyy-MM-dd`, so string order is date order. */
const isInvoiceDateBeforePODate = (invoiceDate?: string | null, poDate?: string | null): boolean =>
  !!invoiceDate && !!poDate && DATE_ONLY_PATTERN.test(invoiceDate) && invoiceDate < poDate;

interface IPOBlocPermissionkKeyValueObject {
  request_invoice: boolean;
  upload_invoice: boolean;
  invoice_number: boolean;
  list_attachments: boolean;
  invoice_date: boolean;
  goods_receiving_date: boolean;
  invoice_value?: boolean;
}

interface IPOBlockPermissions {
  central_ops: IPOBlocPermissionkKeyValueObject;
  clinic_ops: IPOBlocPermissionkKeyValueObject;
}

const INITIAL_PERMISSIONS: IPOBlockPermissions = {
  central_ops: {
    request_invoice: true,
    upload_invoice: false,
    invoice_number: true,
    invoice_date: true,
    goods_receiving_date: false,
    list_attachments: true,
    invoice_value: false,
  },
  clinic_ops: {
    request_invoice: false,
    upload_invoice: true,
    invoice_number: true,
    invoice_date: true,
    goods_receiving_date: true,
    list_attachments: true,
    invoice_value: true,
  },
};


const ManageInvoiceFormFields: React.FC<IManageInvoiceFormFieldsProps> = ({
  formHook,
  attachementFieldHook,
  onSubmit,
  isUploading,
  setIsUploading,
}) => {
  const { getRouteKey } = useRouter();
  const { imageUrlToDownload, getCurrentDate } = useUtils();
  const { setVisible } = useModalActions("PO_RAISE_ERROR_MODAL");
  const userPermissions = useAppSelector((state) => state.auth?.userPermissions);
  const { fieldDOBFormat } = useStringConstants();
  // RBAC: PO_UPLOAD_INVOICE_ACTION — legacy roles: clinic_ops, clinic_ops_doggyworld, clinic_ops_bluecity, central_ops
  const canUploadInvoice = useFeatureAccess("PO_UPLOAD_INVOICE_ACTION");
  // RBAC: PO_CREATE_ACTION — legacy roles: clinic_ops_bluecity_PO, central_ops, Category_Ops
  const canCreatePO = useFeatureAccess("PO_CREATE_ACTION");
  // RBAC: PO_ISSUE_IN_INVOICE_UPLOAD_CLINIC_ALERT — legacy roles: clinic_ops, clinic_ops_doggyworld, clinic_ops_bluecity
  const canSeeInvoiceReuploadAlert = useFeatureAccess("PO_ISSUE_IN_INVOICE_UPLOAD_CLINIC_ALERT");
  const { purchaseOrderDetails: PODetails } = useRouteInfo(getRouteKey("INVENTORY", "id"))?.routeState
    ?.state as IInventoryState;
  const [permissions, setPermissions] = useState<IPOBlocPermissionkKeyValueObject | null>(null);
  const { forOnlyNumbersWithDecimal } = useFormValidations();
  const { execute: extractInvoiceDetails } = useExtractInvoiceDetailsAPI();
  // Server-side caveat about the scan (currently: the invoice's GSTIN is not a vendor we hold).
  const [unknownVendorNudge, setUnknownVendorNudge] = useState<string>("");
  // The stored scan flags an unreadable upload: every extracted value needs checking against the file.
  const ocrAnalysis = PODetails?.invoice_details?.ocr?.analysis;
  const isLowQualityScan = Boolean(ocrAnalysis?.low_image_quality);
  const hasGoodsIssueStamp = Boolean(ocrAnalysis?.goods_issue_stamp);
  const isInvoiceDateAndNumberEditable =
    !!PODetails?.po_status &&
    INVOICE_DATE_AND_NUMBER_EDITABLE_STATUSES_FOR_PO.includes(PODetails?.po_status);
  const poDate = getPODate(PODetails?.created_at);
  // Warning only: an invoice dated before its PO is flagged inline but doesn't block the PO.
  const isInvoiceDateBeforePO = isInvoiceDateBeforePODate(formHook.watch("invoice_details.invoice_date"), poDate);

  useEffect(() => {
    if (!userPermissions || !PODetails) return;
    if (canUploadInvoice) {
      setPermissions(INITIAL_PERMISSIONS.clinic_ops);
    }
    if (canCreatePO) {
      setPermissions(INITIAL_PERMISSIONS.central_ops);
    }
  }, [PODetails, userPermissions, canUploadInvoice, canCreatePO]);

  const showInvoiceFile = (val: string) => {
    if (val) {
      const type = val?.split(".");
      const isTypePdf = type[type?.length - 1];
      if (isTypePdf?.toLowerCase() === "pdf") {
        return <FaRegFilePdf className="w-4 h-4 text-red-500" />;
      }
      return <BiImage className="w-4 h-4 text-blue-500" />;
    }
    return null;
  };

  const handleRaiseIssueSubmit = (issue: string) => {
    formHook.setValue("raised_error_reason", issue);
    onSubmit(PODetails?.po_status === "closed" ? "issue_in_grn" : "issue_in_invoice_upload");
  };

  useEffect(() => {
    formHook.setValue("invoice_details.recorded_date", getCurrentDate());
  }, [PODetails]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (isUploading) return;
      setIsUploading(true);
      setUnknownVendorNudge("");
      try {
        const result = await extractInvoiceDetails(file, PODetails?.po_id);
        if (result.status && result.data) {
          const invoiceData = result.data;
          // The server reads the header now and the products in the background: its message says
          // what to check and to refresh the page in a few minutes for the items.
          if (result.message) {
            setUnknownVendorNudge(result.message);
          }
          formHook.setValue("invoice_details.invoice_date", invoiceData.invoice_date || "");
          formHook.setValue("invoice_details.invoice_number", invoiceData.invoice_number || "");
          formHook.setValue("invoice_details.invoice_value", invoiceData.invoice_value || "");
          formHook.setValue("attachments", [{ file_name: file.name, invoice_url: invoiceData.s3_url }]);
          attachementFieldHook.replace([{ file_name: file.name, invoice_url: invoiceData.s3_url }]);
          formHook.clearErrors("invoice_details.invoice_date");
          formHook.clearErrors("invoice_details.invoice_number");
          formHook.clearErrors("invoice_details.invoice_value");
        }
      } catch (error) {
        console.error("Error extracting invoice details:", error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  return (
    permissions &&
    isObject(PODetails) &&
    INVOICE_DATE_AND_NUMBER_EDITABLE_STATUSES_FOR_PO.includes(PODetails?.po_status) && (
      <div>
        <div className="flex justify-between items-center">
          <h2 className="mt-8 font-semibold text-sm text-[#1F1F23]">Manage Invoices</h2>
          <div>
            {permissions?.request_invoice && PODetails.po_status === "pending_invoice_verification" && (
              <div>
                <button
                  className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#F1F1F1] border border-gray-200 text-[#1F1F23] hover:bg-gray-200"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    setVisible();
                  }}
                >
                  Request New Invoice
                </button>
              </div>
            )}
          </div>
        </div>
        {PODetails?.po_status === "issue_in_invoice_upload" && canSeeInvoiceReuploadAlert && (
            <div className="bg-red-100 my-3 p-3 rounded-md">
              <p className="font-bold text-sm text-red-500">Re-upload Invoice</p>
              <p className="text-red-400 text-xs 2xl:text-sm"> Comment: {PODetails?.raised_error_reason}</p>
            </div>
          )}
        {PODetails?.po_status === "issue_in_invoice_upload" && canCreatePO && (
          <div className="mt-5">
            <AlertError
              title="Pending from clinic to upload new invoice"
              description={
                <div className="text-xs">
                  <p className="flex justify-start items-center gap-x-3 font-semibold text-xs 2xl:text-sm">
                    <span>Reason Raised By You</span>
                    <span className="flex justify-start items-center gap-x-2">
                      <ArrowRightIcon className="w-3 h-3" /> <span>{PODetails?.raised_error_reason}</span>{" "}
                    </span>
                  </p>
                </div>
              }
            />
          </div>
        )}
        {hasGoodsIssueStamp && (
          <div className="mt-5">
            <AlertError
              title="Invoice is stamped for a delivery issue"
              description={
                <div className="text-xs 2xl:text-sm">
                  <p>
                    This invoice carries the “Short/Damaged/Expired/Wrong Item Issue” stamp, so it may
                    not describe what was actually received. Check the quantities against the delivery
                    before you save.
                  </p>
                </div>
              }
            />
          </div>
        )}
        {isLowQualityScan && (
          <div className="mt-5">
            <AlertError
              title="Invoice scan quality is low"
              description={
                <div className="text-xs 2xl:text-sm">
                  <p>
                    {ocrAnalysis?.image_clarity_reason ||
                      "The uploaded invoice was hard to read, so the extracted details may be wrong."}
                  </p>
                  <p className="mt-1">Check every value against the uploaded document before saving.</p>
                </div>
              }
            />
          </div>
        )}
        {unknownVendorNudge && (
          <div className="mt-5">
            <AlertError title="Different vendor on this invoice" description={unknownVendorNudge} />
          </div>
        )}
        {(permissions?.upload_invoice &&
          (PODetails?.po_status === "pending_invoice_upload" || PODetails?.po_status === "issue_in_invoice_upload")) ||
          PODetails?.po_status === "closed" ? (
          <div className="flex items-center">
            <div className="w-2/12">
              <p className="text-xs 2xl:text-sm">
                Upload Invoice <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="inline-flex flex-col justify-start items-start gap-10 py-5">
              <label htmlFor="imageUpload" className="flex items-center gap-[3.73px] pr-[26px] cursor-pointer">
                <div className="relative inline-block">
                  <div className={`${isUploading ? "pointer-events-none opacity-50" : ""}`}>
                    <ImageUploadComponent
                      handleFileChange={handleFileUpload}
                      text={isUploading ? "Uploading invoice, please wait..." : "Upload lab report as doc, image etc."}
                      data={formHook.watch("attachments")}
                      required={true}
                    />
                  </div>
                  {isUploading && (
                    <p className="text-xs text-blue-500 mt-2 flex items-center gap-2">
                      <svg
                        className="animate-spin h-4 w-4 text-blue-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                      </svg>
                      Scanning, please wait...
                    </p>
                  )}
                </div>
              </label>
            </div>
          </div>
        ) : null}
        <div className="flex items-center">
          <div className="w-2/12">
            <p className="text-xs 2xl:text-sm">
              Attachment <span className="text-red-400">*</span>
            </p>
          </div>
          <table className="bg-white w-full">
            {((permissions?.upload_invoice && PODetails?.po_status === "pending_invoice_upload") ||
              PODetails?.po_status === "issue_in_invoice_upload" || PODetails?.po_status === "closed") &&
              canUploadInvoice &&
              (attachementFieldHook.fields.length === 0 ? (
                <p className="pl-4 text-gray-500 text-xs">New attachments have not been uploaded yet. </p>
              ) : (
                <tbody className="flex items-center gap-3 ml-8">
                  {formHook.watch("attachments")?.map((image: any, index) => (
                    <div key={index} className="">
                      <div className="flex flex-col  gap-4">
                        <div className="relative w-10 h-10">
                          {image?.invoice_url && image?.invoice_url?.split(".")?.pop()?.toLowerCase() === "pdf" ? (
                            <>
                              <img
                                src={
                                  "https://vetic-img.s3.ap-south-1.amazonaws.com/quick_commerce//misc/pdf-svgrepo-com.svg"
                                }
                                alt={image?.file_name}
                                className="w-[50px] h-[50px] object-cover"
                              />
                            </>
                          ) : (
                            <>
                              {" "}
                              <img
                                src="https://vetic-img.s3.ap-south-1.amazonaws.com/quick_commerce//misc/pdf-svgrepo-com.svg"
                                alt="Prescription"
                                className="w-[50px] h-[50px] object-cover"
                              />
                            </>
                          )}
                        </div>
                        <p
                          className="text-blue-500 cursor-pointer text-[10px] sm:text-xs "
                          onClick={() => window.open(image?.invoice_url, "_blank")}
                        >
                          {(image?.invoice_url && image?.file_name) ?? "No image available"}
                        </p>
                      </div>
                    </div>
                  ))}
                </tbody>
              ))}
            {permissions?.list_attachments && (
              <>
                {PODetails?.invoice_details?.attachments?.length > 0 && (
                  <p className="flex items-center gap-2 my-2 ml-5 pl-3 font-medium text-[#808DA0] text-xs">
                    <ClockIcon className="w-4 h-4" />
                    Previously Uploaded Files (this will remove automatically once you submit)
                  </p>
                )}
                <tbody>
                  {PODetails?.invoice_details?.attachments?.map((file) => (
                    <tr>
                      <td className="flex items-center gap-8 px-4 py-2 ml-5">
                        <div className="flex items-center gap-1 w-60">
                          {showInvoiceFile(file?.invoice_url)}
                          <p className="w-64 text-xs truncate">{file?.file_name}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => imageUrlToDownload(file?.invoice_url, file?.file_name)}
                          className="text-gray-500"
                        >
                          <IoMdEye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </>
            )}
          </table>
        </div>
        <div className="flex items-center mt-5">
          <div className="w-2/12">
            <p className="text-xs 2xl:text-sm">Recorded Date</p>
          </div>
          <div className="w-40">
            {!permissions?.invoice_number ? (
              <p className="text-xs 2xl:text-sm">{formHook.watch("invoice_details.recorded_date")}</p>
            ) : (
              <input
                type="text"
                readOnly
                className="border-none ring-0 focus-within:ring-0 text-sm outline-none px-0"
                {...formHook.register("invoice_details.recorded_date")}
              />
            )}
          </div>
        </div>
        <div className="flex items-center mt-7">
          <div className="w-2/12">
            <p className="text-xs 2xl:text-sm">
              Goods Receiving Date <span className="text-red-400">*</span>{" "}
            </p>
          </div>
          <div className="w-40">
            {PODetails?.po_status === "pending_invoice_upload" || PODetails?.po_status === "issue_in_invoice_upload" ? (
              <PWLCalendarComponent
                allowPastDaySelection={true}
                errorMessages={[{ message: "Invoice Date is required", type: "required" }]}
                register={formHook.register("invoice_details.goods_receiving_date", {
                  required: true,
                })}
                disabledFutureDateYearMonthSelection={false}
                disableDatesAfterDate={getCurrentDate()}
                fieldError={formHook?.formState?.errors?.invoice_details?.goods_receiving_date ?? null}
                onSelectDate={(date) => {
                  formHook.setValue("invoice_details.goods_receiving_date", format(date, fieldDOBFormat));

                  formHook.clearErrors("invoice_details.goods_receiving_date");
                }}
                defaultSelectedDate={formHook.getValues("invoice_details.goods_receiving_date")}
                className="w-full"
                disabled={!permissions?.goods_receiving_date}
              />
            ) : (
              <p className="text-xs 2xl:text-sm">{PODetails?.invoice_details?.goods_receiving_date}</p>
            )}
          </div>
        </div>
        <div className="flex items-center mt-5">
          <div className="w-2/12">
            <p className="text-xs 2xl:text-sm">
              Invoice Date <span className="text-red-400">*</span>{" "}
            </p>
          </div>
          <div className="w-40">
            {permissions?.invoice_date && isInvoiceDateAndNumberEditable ? (
              <PWLCalendarComponent
                allowPastDaySelection={true}
                errorMessages={[{ message: "Invoice Date is required", type: "required" }]}
                register={formHook.register("invoice_details.invoice_date", {
                  required: true,
                })}
                disabledFutureDateYearMonthSelection={false}
                disableDatesAfterDate={getCurrentDate()}
                fieldError={formHook?.formState?.errors?.invoice_details?.invoice_date ?? null}
                showTypedErrors
                onSelectDate={(date) => {
                  formHook.setValue("invoice_details.invoice_date", format(date, fieldDOBFormat));
                  formHook.clearErrors("invoice_details.invoice_date");
                }}
                defaultSelectedDate={formHook.getValues("invoice_details.invoice_date")}
                className="w-full"
                disabled={!permissions?.invoice_date}
              />
            ) : (
              <p className="text-xs 2xl:text-sm">{PODetails?.invoice_details?.invoice_date}</p>
            )}
            {isInvoiceDateBeforePO && poDate && (
              <span className="block mt-1 w-full text-[9px] text-left text-red-500">
                Invoice Date can&apos;t be before the PO date ({format(parseISO(poDate), "dd-MM-yyyy")})
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center my-4 mt-7">
          <div className="w-2/12">
            <p className="text-xs 2xl:text-sm">
              Invoice Number <span className="text-red-400">*</span>{" "}
            </p>
          </div>
          {permissions?.invoice_number && isInvoiceDateAndNumberEditable ? (
            <Input
              register={formHook?.register("invoice_details.invoice_number", { required: true })}
              fieldError={formHook?.formState?.errors?.invoice_details?.invoice_number ?? null}
              errorMessages={[{ message: "Invoice Number Required", type: "required" }]}
              placeholder="Invoice Number"
              className="w-60"
            />
          ) : (
            <p className="text-xs 2xl:text-sm">{PODetails?.invoice_details?.invoice_number}</p>
          )}
        </div>
        {canUploadInvoice && (
          <div className="flex items-center my-4 mt-7">
            <div className="w-2/12">
              <p className="text-xs 2xl:text-sm">
                Invoice Value <span className="text-red-400">*</span>{" "}
              </p>
            </div>
            {PODetails?.po_status === "pending_invoice_upload" || PODetails?.po_status === "issue_in_invoice_upload" ? (
              <Input
                register={formHook?.register("invoice_details.invoice_value", {
                  required: true,
                  ...forOnlyNumbersWithDecimal?.validations,
                  setValueAs: (val) => Number(val) || 0,
                })}
                fieldError={formHook?.formState?.errors?.invoice_details?.invoice_value ?? null}
                errorMessages={[
                  forOnlyNumbersWithDecimal?.errors,
                  { message: "Invoice Value is required", type: "required" },
                  { type: "validate", message: "Only numbers are allowed" },
                ]}
                placeholder="Invoice Value"
                className="w-60"
              />
            ) : (
              <p className="text-xs 2xl:text-sm">{PODetails?.invoice_details?.invoice_value}</p>
            )}
          </div>
        )}
        <PORaiseErrorModal
          PONumber={PODetails?.po_number}
          POStatus={PODetails?.po_status}
          onSubmit={handleRaiseIssueSubmit}
        />
      </div>
    )
  );
};

export default ManageInvoiceFormFields;
